import React, { useEffect, useMemo, useState } from 'react';
import { Building, LinkedinSquare, World } from '@saleshandy/icons';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import MultiChipInput from '../../../../../shared/components/multi-chip-input';

type Config = {
  key: string;
  icon: React.ReactNode;
  labelText: string;
  placeholder: string;
  showExactMatchCheckbox?: boolean;
  maxInputCount?: number;
};

const COMPANY_BULK_SEARCH_CONFIG: Record<string, Config> = {
  company_name: {
    key: 'company_name',
    icon: <Building />,
    labelText: 'Company Name',
    placeholder:
      'Enter list of Company Name separated by commas, semicolon or line breaks.',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
  company_domain: {
    key: 'company_domain',
    icon: <World />,
    labelText: 'Company Domain',
    placeholder: 'Enter Company Domain',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
  linkedin_url: {
    key: 'linkedin_url',
    icon: <LinkedinSquare />,
    labelText: 'LinkedIn URL',
    placeholder:
      'Enter LinkedIn URL (e.g. https://www.linkedin.com/in/john-doe) separated by commas, semicolon or line breaks.',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
};

type Props = {
  show: boolean;
  focusKey?: string;
  pendingFilters: URLSearchParams;
  onClose: () => void;
  onSearch: (params: {
    filterKey: string;
    values: string[];
    showExactMatch: boolean;
  }) => void;
};

const normalize = (v: string) => v.replace(/"/g, '').trim();

const CompanyFilterBulkSearch: React.FC<Props> = ({
  show,
  focusKey,
  pendingFilters,
  onClose,
  onSearch,
}) => {
  const config = useMemo(() => {
    if (!focusKey) return null;
    return COMPANY_BULK_SEARCH_CONFIG[focusKey] ?? null;
  }, [focusKey]);

  const [values, setValues] = useState<string[]>([]);
  const [showExactMatch, setShowExactMatch] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!show || !config) return;

    const queryValues = pendingFilters.getAll(config.key).map(normalize);
    const exactMatch = pendingFilters.get(`${config.key}_exactMatch`) === '1';
    setValues(queryValues);
    setShowExactMatch(exactMatch);
    setError('');
  }, [show, config, pendingFilters]);

  const isLimitExceeded = config && values.length > (config.maxInputCount ?? 0);

  const onSubmit = () => {
    if (!config || isLimitExceeded) return;
    onSearch({
      filterKey: config.key,
      values,
      showExactMatch,
    });
  };

  return (
    <Modal
      show={show}
      titleContent="Bulk Search"
      showCloseIcon={true}
      submitButtonText="Search"
      onHide={onClose}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitDisabled={isLimitExceeded}
      className="v2-leads-filter-bulk-search"
    >
      {config ? (
        <div key={config.key}>
          <div className="search-input-label-container">
            <div className="search-input-label">
              <div className="label-icon">{config.icon}</div>
              <div className="label">{config.labelText}</div>
            </div>
            {config.showExactMatchCheckbox ? (
              <Checkbox
                value={config.key}
                className="show-exact-match-check"
                onChange={(checked) => setShowExactMatch(checked)}
                checked={showExactMatch}
                disabled={values.length === 0}
                label={
                  <div className="label-container d-inline-block">
                    <p className="checkbox-label-text m-0">Show Exact Match</p>
                  </div>
                }
              />
            ) : null}
          </div>
          <MultiChipInput
            values={values}
            onChange={setValues}
            error={error}
            onError={setError}
            placeholder={config.placeholder}
            showInputCount={true}
            maxInputCount={config.maxInputCount}
            scrollTo={true}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export default CompanyFilterBulkSearch;
